import { useMemo } from "react";
import { Link, useParams } from "react-router";
import { TopBar } from "../components/TopBar";
import { Tile } from "../components/shared/Tile";
import { formatDollars } from "../lib/format";
import { conditionHref, sessionsHref } from "../lib/links";
import { moodForEffortOrCost, moodForSuccess } from "../lib/moods";
import {
  type DayBucket,
  type LeaderboardRow,
  bucketByDay,
  computeStats,
  leaderboard,
  topConditions,
} from "../lib/stats";
import type { ManifestSession } from "../models";
import { CONDITION_SHORT_NAMES } from "../models/condition-short-names";
import { CONDITION_NAMES } from "../models/conditions";
import { useManifestStore } from "../state/manifestStore";
import { useSettings } from "../state/settingsStore";

const WINDOW_DAYS = 30;

export function AnalyticsPage() {
  const { tenant } = useParams();
  const manifest = useManifestStore((s) => s.manifest);
  const syntheticOnly = useSettings((s) => s.syntheticOnly);

  const sessions = useMemo(() => {
    if (!manifest || !tenant) return [] as ManifestSession[];
    let arr = manifest.sessions.filter((s) => s.tenant === tenant);
    if (syntheticOnly) arr = arr.filter((s) => s.synthetic);
    return arr;
  }, [manifest, tenant, syntheticOnly]);

  const stats = useMemo(() => computeStats(sessions), [sessions]);
  const days = useMemo(() => bucketByDay(sessions, WINDOW_DAYS), [sessions]);
  const conds = useMemo(() => topConditions(sessions), [sessions]);
  const byProject = useMemo(() => leaderboard(sessions, (s) => s.project), [sessions]);
  const byUser = useMemo(() => leaderboard(sessions, (s) => s.user), [sessions]);

  if (!manifest || !tenant) return null;

  const projectNameById = new Map(
    manifest.projects.filter((p) => p.tenant === tenant).map((p) => [p.id, p.name]),
  );
  const userNameById = new Map(
    manifest.users.filter((u) => u.tenant === tenant).map((u) => [u.id, u.name]),
  );

  return (
    <>
      <TopBar tenant={tenant} title="Analytics" />
      <div className="app-content">
        <section>
          <h3 className="section-h">
            Last {WINDOW_DAYS} days{syntheticOnly ? " · synthetic only" : ""}
          </h3>
          <div className="vitals">
            <Tile label="Sessions" value={stats.sessions.toLocaleString()} />
            <Tile
              label="Success"
              value={`${stats.meanSuccess}%`}
              mood={moodForSuccess(stats.meanSuccess)}
            />
            <Tile
              label="Effort"
              value={`${stats.meanEffort}`}
              mood={moodForEffortOrCost(stats.meanEffort)}
            />
            <Tile label="AI cost" value={formatDollars(stats.totalDollars)} />
          </div>
        </section>

        <section className="card">
          <div className="card-header">
            <h2>Sessions per day</h2>
            <span className="meta-label mono">
              total {stats.sessions.toLocaleString()} · {stats.totalTokens.toLocaleString()} tokens
            </span>
          </div>
          <DayChart days={days} />
        </section>

        <section className="card">
          <div className="card-header">
            <h2>Condition mix</h2>
            <span className="meta-label mono">{conds.length} conditions present</span>
          </div>
          {conds.length === 0 ? (
            <p className="empty">No findings in the selected scope.</p>
          ) : (
            <div className="cond-bars">
              {conds.map((c) => {
                const max = conds[0]?.count ?? 1;
                return (
                  <Link
                    key={c.code}
                    to={sessionsHref(tenant, { issue: c.code })}
                    className="cond-bar"
                    title={`Open Sessions filtered by ${conditionName(c.code)}`}
                  >
                    <span className="cond-bar-name">
                      <Link to={conditionHref(tenant, c.code)} onClick={(e) => e.stopPropagation()}>
                        {CONDITION_SHORT_NAMES[c.code] ?? c.code}
                      </Link>
                    </span>
                    <span className="cond-bar-track">
                      <span
                        className={`cond-bar-fill cat-${c.code[0].toLowerCase()}`}
                        style={{ width: `${(c.count / max) * 100}%` }}
                      />
                    </span>
                    <span className="cond-bar-value mono">{c.count}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </section>

        <div className="leaderboard-row">
          <section className="card">
            <div className="card-header">
              <h2>Top projects</h2>
              <span className="meta-label mono">by session count</span>
            </div>
            <Leaderboard
              rows={byProject}
              labelFor={(k) => projectNameById.get(k) ?? k}
              linkFor={(k) => sessionsHref(tenant, { project: k })}
            />
          </section>
          <section className="card">
            <div className="card-header">
              <h2>Top users</h2>
              <span className="meta-label mono">by session count</span>
            </div>
            <Leaderboard
              rows={byUser}
              labelFor={(k) => userNameById.get(k) ?? k}
              linkFor={(k) => sessionsHref(tenant, { user: k })}
            />
          </section>
        </div>
      </div>
    </>
  );
}

function DayChart({ days }: { days: DayBucket[] }) {
  const max = Math.max(1, ...days.map((d) => d.count));
  return (
    <div className="day-chart">
      <svg
        viewBox={`0 0 ${days.length * 24} 100`}
        preserveAspectRatio="none"
        className="day-chart-svg"
        role="img"
        aria-label="Sessions per day bar chart"
      >
        {days.map((d, i) => {
          const h = (d.count / max) * 90;
          const x = i * 24 + 2;
          const y = 95 - h;
          return (
            <g key={d.date}>
              <rect x={x} y={y} width={20} height={h} className="day-bar">
                <title>{`${d.date}: ${d.count} sessions, ${d.tokens.toLocaleString()} tokens, ${formatDollars(d.dollars)}`}</title>
              </rect>
            </g>
          );
        })}
      </svg>
      <div className="day-chart-axis">
        <span>{days[0]?.date}</span>
        <span>{days[Math.floor(days.length / 2)]?.date}</span>
        <span>{days[days.length - 1]?.date}</span>
      </div>
    </div>
  );
}

function Leaderboard({
  rows,
  labelFor,
  linkFor,
}: {
  rows: LeaderboardRow[];
  labelFor: (k: string) => string;
  linkFor: (k: string) => string;
}) {
  if (rows.length === 0) return <p className="empty">No data.</p>;
  const max = rows[0]?.count ?? 1;
  return (
    <div className="cond-bars">
      {rows.slice(0, 10).map((r) => (
        <Link key={r.key} to={linkFor(r.key)} className="cond-bar">
          <span className="cond-bar-name">{labelFor(r.key)}</span>
          <span className="cond-bar-track">
            <span
              className="cond-bar-fill cat-leader"
              style={{ width: `${(r.count / max) * 100}%` }}
            />
          </span>
          <span className="cond-bar-value mono">
            {r.count} ·{" "}
            <span className={`mood-${moodForSuccess(r.meanSuccess)}`}>{r.meanSuccess}</span>
          </span>
        </Link>
      ))}
    </div>
  );
}

function conditionName(code: string): string {
  return CONDITION_NAMES[code as keyof typeof CONDITION_NAMES] ?? code;
}
